<template>
  <div class="forge-file-viewer">
    <v-dialog
      v-model="isOpen"
      fullscreen
      scrollable
    >
    <v-card v-if="item" class="fm-document-viewer pa-0">
      <div class="d-flex align-center justify-space-between fm-document-header">
				<img :src="require('@/assets/Svg/logoWhite.svg')" style="max-width: 110px; width: 100%;"/>
        <div v-if="!isMobile" class="d-flex align-center fm-document-header-title">
          <v-icon class="mr-3" color="white">{{ getFileIcon }}</v-icon>
          <div class="font-weight-medium text-truncate">{{ item.name }}</div>
				</div>
        <div class="fm-document-header-primary">
          <div class="fm-document-header-primary-download">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click="downloadFile"
                  color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Descargar archivo</span>
            </v-tooltip>
          </div>
          <v-btn icon small @click="$emit('minimize-file', item)" class="ml-1 fm-document-header-primary-minimize" color="white">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <v-btn icon small @click="closeDialog" class="ml-1 fm-document-header-primary-close fm-document-header-close" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="d-flex align-center fm-document-header-actions" style="gap: 8px;">
          <div class="fm-document-header-secondary">
					<v-chip small outlined color="white" label v-if="!isMobile">{{ getFileInfo }}</v-chip>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								icon
								small
								@click="associationsOpen = !associationsOpen"
								color="white"
								v-bind="attrs"
								v-on="on"
							>
								<v-icon>mdi-link-variant</v-icon>
							</v-btn>
						</template>
						<span>Asociaciones</span>
					</v-tooltip>
          <RevisionFileSelector
            :revision-context="revisionContext"
            :current-file-id="item.id"
            :loading="revisionLoading"
            @select="$emit('select-revision', $event)"
          />
          <v-btn v-if="canLoadForgeViewer" icon small @click="openInNewTab" class="ml-1" color="white">
						<v-icon>mdi-open-in-new</v-icon>
					</v-btn>
          </div>
				</div>
      </div>
      <v-card-text class="fm-document-content pa-0">
        <ForgeViewer v-if="canLoadForgeViewer" :item="item" @load="onForgeLoad" />
        <div v-else class="forge-conversion-state">
          <v-icon color="#2D4B95" size="48">mdi-cube-scan</v-icon>
          <div class="forge-conversion-state-title">
            {{ isProcessing ? 'Archivo en proceso de conversión' : 'Archivo no convertido' }}
          </div>
          <div class="forge-conversion-state-text">
            {{ isProcessing ? 'Podrás visualizarlo cuando termine el procesamiento.' : 'Este archivo debe convertirse antes de poder visualizarse en Autodesk.' }}
          </div>
          <v-btn
            v-if="canConvert"
            color="primary"
            :loading="conversionLoading"
            :disabled="isProcessing"
            @click="promptConversion"
          >
            <v-icon left>mdi-send</v-icon>
            Enviar a Autodesk
          </v-btn>
          <div v-else-if="!isProcessing" class="forge-conversion-permission">
            <v-icon small class="mr-1">mdi-lock-outline</v-icon>
            No tienes permiso para enviar este archivo a Autodesk.
          </div>
        </div>
      </v-card-text>
			<AssociationsListModal v-model="associationsOpen" :file-id="item.id" />
    </v-card>
    </v-dialog>
    <AcceptCancelModal
      :acceptRejectModal="forgeConfirmModal"
      :acceptRejectModalData="forgeConfirmData"
      :closeModal="handleForgeConfirmation"
    />
  </div>
</template>

<script>
import ForgeViewer from '@/components/Views/Planimetria/ForgeViewer'
import AssociationsListModal from '@/components/Globals/Helpers/AssociationsListModal'
import RevisionFileSelector from '@/components/Globals/Helpers/RevisionFileSelector'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal'
import { addFileToForge } from '@/helpers/api/forge'
import { checkPermissions } from '@/helpers/permissions'

export default {
  name: 'ForgeFileViewer',
  components: { ForgeViewer, AssociationsListModal, RevisionFileSelector, AcceptCancelModal },
  props: {
    item: {
      type: Object,
      default: null
    },
    revisionContext: {
      type: Object,
      default: null
    },
    revisionLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadFailed: false,
      isOpen: false,
			loading: true,
			associationsOpen: false,
      conversionLoading: false,
      forgeConfirmModal: false,
      forgeConfirmData: null,
      forgeItemToSend: null
    }
  },
  watch: {
    item: {
      immediate: true,
      handler(newVal) {
        this.isOpen = Boolean(newVal)
        this.setForgeSelectedFile(newVal)
      }
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
    getFileIcon() {
      return 'mdi-file-cad'
    },
    getFileInfo() {
      const ext = this.item?.ext?.toUpperCase() || 'ARCHIVO'
      const size = this.getFileSize()
      return `${ext}${size ? ` • ${size}` : ''}`
    },
    isForgeItem() {
      return this.item?.file_type === 'forge' || Boolean(this.item?.forge_file)
    },
    hasForgeUrn() {
      return Boolean(this.item?.forge_file?.translated && this.item?.forge_file?.urn)
    },
    canLoadForgeViewer() {
      return this.isForgeItem && this.hasForgeUrn
    },
    isProcessing() {
      const processingFiles = this.$store?.globalStore?.state?.forgeProcessingFiles || {}
      return Boolean(this.item?.forge_file?.processing || processingFiles[this.item?.id])
    },
    canConvert() {
      return this.isForgeItem && !this.isProcessing && checkPermissions(['send_to_autodesk'])
    }
  },
  methods: {
    setForgeSelectedFile(item) {
      if (!this.$store?.forgeStore) return
      this.$store.forgeStore.commit('setForgeSelectedFile', item && this.hasForgeUrn ? item : null)
    },
    promptConversion() {
      if (!this.item || this.isProcessing) return
      if (!checkPermissions(['send_to_autodesk'])) {
        this.notify('No tienes permisos para enviar este archivo a Autodesk.', 'Acceso denegado')
        return
      }

      this.forgeItemToSend = this.item
      this.forgeConfirmData = {
        title: 'Enviar a visor Autodesk',
        content_a: `¿Deseas enviar "${this.item.name}" al visor Autodesk?`,
        content_b: 'El archivo será procesado y podrá visualizarse después.'
      }
      this.forgeConfirmModal = true
    },
    async handleForgeConfirmation(confirmed) {
      const item = this.forgeItemToSend
      this.forgeConfirmModal = false
      this.forgeConfirmData = null
      this.forgeItemToSend = null

      if (!confirmed || !item || this.isProcessing) return
      if (!checkPermissions(['send_to_autodesk'])) {
        this.notify('No tienes permisos para enviar este archivo a Autodesk.', 'Acceso denegado')
        return
      }

      this.conversionLoading = true
      try {
        const response = await addFileToForge({ file_id: item.id })
        if (response?.code === 200) {
          const fileName = item.name || item.title || item.filename || `Archivo ${item.id}`
          this.$store.globalStore.commit('setForgeProcessingFile', {
            file_id: item.id,
            file_name: fileName,
            title: fileName
          })
          this.notify('El archivo fue enviado a conversión.', 'Conversión iniciada', 'success')
        } else {
          this.notify('No fue posible enviar el archivo a conversión.', 'Error de conversión')
        }
      } catch (error) {
        this.notify('No fue posible enviar el archivo a conversión.', 'Error de conversión')
      } finally {
        this.conversionLoading = false
      }
    },
    notify(message, title, type = 'warning') {
      this.$store?.globalStore?.dispatch('addMessage', {
        message,
        title,
        created: new Date(),
        type,
        icon: type === 'success' ? 'mdi-check-circle-outline' : 'mdi-lock-outline',
        color: type === 'success' ? '#2E8B57' : '#FB8500'
      })
    },
    onForgeLoad() {
      this.$emit('load', { item: this.item, viewer: 'forge' })
    },
    downloadFile() {
      if (!this.item?.url) return
      this.$emit('download', { item: this.item, viewer: 'forge' })
      window.open(this.item.url, '_blank')
    },
		openInNewTab() {
			if (this.item) {
				const width = window.outerWidth;
				const height = window.outerHeight;
				const left = window.screenX;
				const top = window.screenY;
				const url = 'forge-viewer?item_id=' + this.item.id;
				window.open(
					url,
					'_blank',
					`width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
				);
				this.closeDialog();
			}
		},
    onIframeError() {
      this.loadFailed = true
    },

    onIframeLoad() {
      this.loadFailed = false
    },

    closeDialog() {
      this.isOpen = false
			this.associationsOpen = false
      this.forgeConfirmModal = false
      this.setForgeSelectedFile(null)
			this.$emit('close')
			window.close()
    },
    getFileSize() {
      const size = this.item?.size
      if (!size) return null
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
      return `${(size / (1024 * 1024)).toFixed(2)} MB`
    }
  }
}
</script>

<style scoped>
.fm-document-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.fm-document-header {
  border-bottom: 1px solid #213872;
  padding: 8px 16px !important;
  background: #2D4B95;
	color: white;
}

.fm-document-header .v-toolbar__content {
  padding: 0 !important;
}

.fm-document-content {
  flex: 1;
  overflow-y: auto;
  background: #fafafa;
}

.forge-conversion-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  justify-content: center;
  padding: 32px;
  text-align: center;
}

.forge-conversion-state-title {
  color: #263758;
  font-size: 18px;
  font-weight: 600;
}

.forge-conversion-state-text,
.forge-conversion-permission {
  color: #5a6a8a;
  max-width: 480px;
}

.document-viewer-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-viewer-iframe {
  width: 100%;
  height: 100%;
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  border: none;
}
</style>